import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  DataDictionaryTable,
  DictionaryCode,
  DictionaryField,
} from "content/catalogue";
import { ArrowUpRight, Check, KeyRound } from "lucide-react";

interface DataDictionaryProps {
  tables?: DataDictionaryTable[];
}

const FieldAttribute = ({
  field,
  showKeyGutter,
}: {
  field: DictionaryField;
  showKeyGutter: boolean;
}) => (
  <span className="flex items-center gap-2">
    {field.primaryKey ? (
      <KeyRound
        className="size-3.5 shrink-0 text-amber-600 dark:text-amber-500"
        aria-hidden
      />
    ) : field.foreignKey ? (
      <ArrowUpRight
        className="size-3.5 shrink-0 text-blue-600 dark:text-blue-400"
        aria-hidden
      />
    ) : (
      showKeyGutter && <span className="w-3.5 shrink-0" aria-hidden />
    )}
    <span className="font-mono text-xs">{field.name}</span>
    {field.primaryKey && (
      <span className="rounded bg-amber-100 px-1 py-0.5 font-mono text-[10px] font-medium tracking-wide text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
        PK
      </span>
    )}
    {field.foreignKey &&
      (field.references ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              className="cursor-help rounded bg-blue-100 px-1 py-0.5 font-mono text-[10px] font-medium tracking-wide text-blue-700 dark:bg-blue-950/40 dark:text-blue-400"
            >
              FK
            </span>
          </TooltipTrigger>
          <TooltipContent>References {field.references}</TooltipContent>
        </Tooltip>
      ) : (
        <span className="rounded bg-blue-100 px-1 py-0.5 font-mono text-[10px] font-medium tracking-wide text-blue-700 dark:bg-blue-950/40 dark:text-blue-400">
          FK
        </span>
      ))}
  </span>
);

const FieldsTable = ({ fields }: { fields: DictionaryField[] }) => {
  const showKeyGutter = fields.some((f) => f.primaryKey || f.foreignKey);
  const showMandatory = fields.some((f) => f.mandatory);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Attribute</TableHead>
          {showMandatory && (
            <TableHead className="text-center">Mandatory</TableHead>
          )}
          <TableHead>Data Type</TableHead>
          <TableHead>Description</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {fields.map((field) => (
          <TableRow key={field.name}>
            <TableCell className="align-top whitespace-nowrap">
              <FieldAttribute field={field} showKeyGutter={showKeyGutter} />
            </TableCell>
            {showMandatory && (
              <TableCell className="align-top text-center">
                {field.mandatory && (
                  <Check
                    role="img"
                    className="inline size-3.5 text-muted-foreground"
                    aria-label="Mandatory"
                  />
                )}
              </TableCell>
            )}
            <TableCell className="text-muted-foreground align-top whitespace-nowrap">
              {field.dataType}
            </TableCell>
            <TableCell className="align-top whitespace-normal">
              {field.description}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const CodesTable = ({ codes }: { codes: DictionaryCode[] }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead>Code</TableHead>
        <TableHead>Name</TableHead>
        <TableHead>Description</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {codes.map((code) => (
        <TableRow key={code.code}>
          <TableCell className="font-mono text-xs align-top whitespace-nowrap">
            {code.code}
          </TableCell>
          <TableCell className="align-top whitespace-nowrap">
            {code.name}
          </TableCell>
          <TableCell className="align-top whitespace-normal">
            {code.description}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export const CatalogueEntryDataDictionary = ({
  tables,
}: DataDictionaryProps) => {
  if (!tables?.length) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Data Dictionary</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={tables[0].name} className="w-full">
          <TabsList className="w-full justify-start gap-1 overflow-x-auto">
            {tables.map((table) => (
              <TabsTrigger key={table.name} value={table.name}>
                {table.name}
              </TabsTrigger>
            ))}
          </TabsList>
          {tables.map((table) => (
            <TabsContent key={table.name} value={table.name} className="pt-4">
              <div className="overflow-x-auto">
                {table.kind === "fields" ? (
                  <FieldsTable fields={table.fields} />
                ) : (
                  <CodesTable codes={table.codes} />
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};
